import { Marker } from "@googlemaps/markerclusterer";
import { darken, Typography } from "@mui/material";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { useCallback } from "react";
import { slugify } from "../../helpers/strings";
import { useAppSelector } from "../../store/hooks";
import MarkerIcon from "../_include/icons/MarkerIcon";
import { AddressWithErrors } from "../../models/AwarenessCampaign";
import { DANGER_COLORS } from "../../helpers/draw";
import { DangerLevel } from "../../helpers/geo";

type AddressMarkerProps = {
    /**
     * Sequential index displayed on the marker for the mission detail.
     */
    index?: number;
    point?: AddressWithErrors;
    position: google.maps.LatLngLiteral;
    addressKey: string;
    /** The ID of the sensitization address in the case of an awareness campaign. */
    sensitizationAddressID?: string;
    providerId?: string;
    missionID?: string; // only passed on the mission map to identify if the marker is from a mission map
    color: string;
    zIndex: number;
    /**
     * Click handler for the marker.
     * @param addressKey - The unique address key.
     * @param position - Optional marker position.
     * @param providerIDs - Optional list of provider IDs.
     */
    onClick?: (addressKey: string, position: google.maps.LatLngLiteral, providerIDs?: string[], sensitizationAddressID?: string) => void;
    setMarkerRef?: (marker: Marker | null, key: string) => void;
}

const MARKER_SIZE = 35;
const SELECTED_MARKER_SIZE = 40;

export default function AddressMarker({ index, point, position, addressKey, sensitizationAddressID, providerId, missionID, color, zIndex, onClick, setMarkerRef }: AddressMarkerProps) {
    const selected = useAppSelector(state => state.batch.map.selectedAddressKey === addressKey || state.awareness_campaign.map.selectedAddressID === sensitizationAddressID);
    const markerSize = selected ? SELECTED_MARKER_SIZE : MARKER_SIZE;
    const missionSorted = useAppSelector((state) => state.awareness_campaign.addresses.sorted);

    const slugedAddressKey = slugify(addressKey);

    /**
    * Handles the click event on the marker.
    * Toggles the tooltip visibility and triggers data fetch actions for search map.
    */
    const handleMarkerClick = (e: google.maps.MapMouseEvent) => {
        onClick?.(addressKey, position, providerId ? [providerId] : undefined, sensitizationAddressID);
        e.domEvent.preventDefault(); // prevent click event from propagating to other elements
    };

    const ref = useCallback(
        (marker: google.maps.marker.AdvancedMarkerElement | null) => {
            if (setMarkerRef) {
                setMarkerRef(marker, addressKey);
            }
        },
        [setMarkerRef, addressKey]
    );

    // If selected, further boost the z-index.
    const markerZIndex = selected ? zIndex + 99 : zIndex;

    return (
        <AdvancedMarker
            position={position}
            clickable
            onClick={handleMarkerClick}
            ref={ref}
            zIndex={markerZIndex}
        >
            <div style={{ position: 'relative' }}>
                <svg width={0} height={0}>
                    <linearGradient id={`linearColors-${slugedAddressKey}`} x1={1} y1={0} x2={1} y2={1}>
                        {missionID ?      // add the marker color on mission map for to indentify visited or not                           
                            (
                                <>
                                    <stop offset={0} stopColor={point?.visited ? DANGER_COLORS[DangerLevel.NONE] : DANGER_COLORS[DangerLevel.VERY_HIGH]} />
                                    <stop offset={1} stopColor={darken(point?.visited ? DANGER_COLORS[DangerLevel.NONE] : DANGER_COLORS[DangerLevel.VERY_HIGH], .3)} />
                                </>
                            ) : (
                                <>
                                    <stop offset={0} stopColor={color} />
                                    <stop offset={1} stopColor={darken(color, .3)} />
                                </>
                            )
                        }
                    </linearGradient>
                </svg>
                <MarkerIcon
                    sx={{
                        fill: theme => selected ? theme.palette.info.main : `url(#linearColors-${slugedAddressKey})`,
                        cursor: "pointer",
                        fontSize: markerSize,
                        stroke: "none",
                        ...(selected && {
                            stroke: "#fff",
                            strokeWidth: 1,
                        }),
                    }}
                />

                {index !== undefined && missionSorted && !point?.visited && (
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: '-90%',
                            left: '-20%',
                            transform: 'translate(-50%, -50%)',
                            color: '#ffff',
                            fontSize: `${markerSize * 0.4}px`,
                            pointerEvents: 'none',
                            zIndex: 10
                        }}
                    >
                        {index + 1}
                    </Typography>
                )}
            </div>

            {/* </Tooltip> */}
        </AdvancedMarker>
    )
}
